import React, { useMemo } from 'react';

import type { LayoutElement } from '../../types';

import {

  getElement3DExtrusionDepth,

  isDxfEquipmentLayoutElement,

  resolveEquipmentRenderHeight,

} from '../../utils/layout3d';

import { resolveEquipment3DPortFacing } from './amhs/Equipment/equipmentFacing3d';

import { resolveKitDockLayout } from './amhs/Equipment/equipmentPortOwnership';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';

import { selectElement } from '../../features/selection/selectionSlice';

import FabAmhsFloorLayer3D from './FabAmhsFloorLayer3D';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';

import TransferAnimationLayer3D from './TransferAnimationLayer3D';



export function isEquipmentLayoutElement(element: LayoutElement): boolean {

  return (

    element.type === 'stocker' ||

    element.type === 'stb' ||

    element.type === 'ohb' ||

    element.type === 'lifter' ||

    isDxfEquipmentLayoutElement(element)

  );

}



type Props = {

  elements: Record<string, LayoutElement>;

  elementIds: string[];

  selectedIds: string[];

  conflictIds: ReadonlySet<string>;

  stockerFoups: Record<string, number>;

  floorPlanScaleFactor?: number;

  dxfUnitsPerMeter?: number;

  hasFloorCalibration: boolean;

};



/**

 * Unified layout-native equipment path — full procedural kits for every machine.

 */

const LayoutEquipmentLayer3D: React.FC<Props> = ({

  elements,

  elementIds,

  selectedIds: _selectedIds,

  conflictIds: _conflictIds,

  stockerFoups,

  floorPlanScaleFactor: _floorPlanScaleFactor,

  dxfUnitsPerMeter: _dxfUnitsPerMeter,

  hasFloorCalibration: _hasFloorCalibration,

}) => {

  const dispatch = useAppDispatch();

  const equipmentInstances = useAppSelector((s) => s.equipment.instances);



  const descriptors = useMemo(() => {

    const out: EquipmentRenderDescriptor[] = [];

    for (const id of elementIds) {

      const element = elements[id];

      if (!element || !isEquipmentLayoutElement(element)) continue;

      const width = Math.max(0.5, element.dimensions.width);

      const height = Math.max(0.5, element.dimensions.height);

      const extrusion = getElement3DExtrusionDepth(element);

      const kind = element.type === 'stocker' ? 'stocker' : 'equipment';

      const facing = resolveEquipment3DPortFacing(element);

      const docks = kind === 'equipment' ? resolveKitDockLayout(element, equipmentInstances) : null;

      out.push({

        id,

        originX: element.position.x,

        originY: element.position.y,

        width,

        height,

        depth: resolveEquipmentRenderHeight(kind, width, height, extrusion),

        rotationDeg: element.rotation || 0,

        kind,

        facingRotationY: facing.rotationY,

        swapsFootprintAxes: facing.swapsFootprintAxes,

        loadPortCount: docks?.loadPortCount,

        portFractions: docks?.portFractions,

        storedFoupsCount: kind === 'stocker' ? (stockerFoups[id] ?? 0) : 0,

      });

    }

    return out;

  }, [elementIds, elements, equipmentInstances, stockerFoups]);



  if (descriptors.length === 0) return null;

  return (

    <group name="LayoutEquipmentLayer3D">

      <FabAmhsFloorLayer3D
        descriptors={descriptors}
        layoutMeta={elements}
        onSelect={(id) => dispatch(selectElement(id))}
      />

      <TransferAnimationLayer3D descriptors={descriptors} layoutMeta={elements} />

    </group>

  );

};



export default React.memo(LayoutEquipmentLayer3D);

